import React, { useEffect, useState, useRef, cloneElement } from 'react';
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  position?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  className?: string;
}
export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 300,
  className = ''
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0
  });
  const childRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const showTooltip = () => {
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      updatePosition();
    }, delay);
  };
  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  };
  const updatePosition = () => {
    if (!childRef.current || !tooltipRef.current) return;
    const childRect = childRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    let top = 0;
    let left = 0;
    switch (position) {
      case 'top':
        top = childRect.top + scrollTop - tooltipRect.height - 8;
        left = childRect.left + scrollLeft + childRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'right':
        top = childRect.top + scrollTop + childRect.height / 2 - tooltipRect.height / 2;
        left = childRect.right + scrollLeft + 8;
        break;
      case 'bottom':
        top = childRect.bottom + scrollTop + 8;
        left = childRect.left + scrollLeft + childRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = childRect.top + scrollTop + childRect.height / 2 - tooltipRect.height / 2;
        left = childRect.left + scrollLeft - tooltipRect.width - 8;
        break;
    }
    // Make sure tooltip doesn't go off screen
    const padding = 10;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }
    setTooltipPosition({
      top,
      left
    });
  };
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  // Add event listeners to the child element
  const childWithListeners = cloneElement(children, {
    ref: childRef,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip
  });
  // Get tooltip arrow position class
  const getArrowClass = () => {
    switch (position) {
      case 'top':
        return 'tooltip-arrow-bottom';
      case 'right':
        return 'tooltip-arrow-left';
      case 'bottom':
        return 'tooltip-arrow-top';
      case 'left':
        return 'tooltip-arrow-right';
    }
  };
  return <>
      {childWithListeners}
      {isVisible && <div ref={tooltipRef} className={`fixed z-50 px-2 py-1 text-xs font-medium text-gray-100 bg-gray-800 border border-gray-700 rounded shadow-lg ${getArrowClass()} ${className}`} style={{
      top: `${tooltipPosition.top}px`,
      left: `${tooltipPosition.left}px`
    }} role="tooltip" data-id="element-920">
          {content}
        </div>}
    </>;
}